import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { FormControlLabel, Grid, Radio, RadioGroup } from '@material-ui/core';
import ColumnTypeDisplay from 'gridUI/ColumnTypeDisplay';
import { useMemberMappingByEmail } from 'hooks/permission/member';
import { getFullName } from 'utils/name';
import {
    useDependencyList,
    useDisabledSourceColumns,
    useLatestRecordHistoryData,
    useViewColumnIdsWithReOrder
} from 'hooks/gridUI';
import { getFriendlyTime } from 'utils/datetime';
import { DEPENDENCY_STATUS, SOURCE_STATUS, TEXT_LINE_HEIGHT } from 'const/gridUI';
import {
    isKbEscape,
    isKbEnter,
    isKbTab,
    isKbShiftTab,
    isKbShiftEnter,
    isKbPageEnd,
    isKbPageStart
} from 'utils/keyboard';
import { useDispatch } from 'react-redux';
import * as gridUIActions from 'gridUI/actions';
import diff_match_patch from 'diff-match-patch';
import TooltipArrows from 'gridUI/extendViews/common/TooltipArrows';
import { getEditorValueBySplitLineBreak, serializeTag } from 'components/formula/serialize';
import { Editor, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';
import TokenEditor from 'components/tag';
import ShowSpecialCharacter from 'gridUI/extendViews/common/ShowSpecialCharacter';
// import ShowDiffChange from 'gridUI/extendViews/common/ShowDiffChange';
import ShowCellCannotEdit from 'gridUI/extendViews/common/ShowCellCannotEdit';
import useClickAwaitListener from 'hooks/useClickAwaitListener';
import { checkContainId } from 'utils/clickAway';
import * as columnTypes from 'const/columnTypes';
import 'diff-match-patch-line-and-word';
import { useTranslation } from 'react-i18next';
import i18n from 'i18n';
import DependencySVG from 'assets/images/svg/DependencySVG';
import theme from 'theme';
import { getSourceStatusIcon, getSourceStatusLabelByStatus } from 'utils/gridUI/dependency';
import ApplyIconSVG from 'assets/images/svg/ApplyIconSVG';
import { sendManualTrack } from 'tracker';

const diff = new diff_match_patch();

const useStyles = makeStyles(theme => ({
    root: {
        maxWidth: '1920px',
        margin: '0 auto',
        padding: `17px 24px`,
        height: 'auto'
    },
    panel: {
        background: theme.colors.white,
        borderRadius: 8,
        height: `auto`,
        flex: 1
    },
    sidebar: {
        maxWidth: '30%',
        minWidth: 300,
        background: theme.colors.white,
        borderRadius: 8,
        height: `auto`,
        marginLeft: theme.spacing(4),
        padding: 16,
        overflow: 'hidden'
    },
    header: {
        height: '48px',
        paddingLeft: 16,
        paddingRight: 16,
        borderBottom: `1px solid ${theme.colors.border}`
    },
    footer: {
        height: '48px',
        paddingLeft: 16,
        paddingRight: 16,
        borderTop: `1px solid ${theme.colors.border}`
    },
    inputWrapper: {
        padding: 16,
        position: 'relative'
    },
    multiText: {
        width: '100%',
        padding: `16px 12px`,
        borderRadius: 8,
        overflow: 'auto',
        textOverflow: 'ellipsis',
        fontSize: '0.875rem',
        // resize: 'vertical !important',
        border: `1px solid ${theme.colors.border}`,
        '&:focus': {
            outline: 'none',
            border: `1px solid ${theme.colors.highlight}`
        },
        '& *': {
            lineHeight: `${TEXT_LINE_HEIGHT}px`
        },
        '& p': {
            marginTop: 0,
            marginBottom: 0
        }
    },
    disabled: {
        background: theme.colors.ghostwhite,
        border: 0
    },
    icon: {
        width: 24,
        height: 24,
        borderRadius: 4,
        background: theme.colors.solitude,
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer'
    },
    diff: {
        margin: 0,
        width: '100%',
        border: `1px solid ${theme.colors.border}`,
        background: theme.colors.backgroundDisabled,
        borderRadius: 8,
        padding: 12,
        '& *': {
            lineHeight: `${TEXT_LINE_HEIGHT}px`
        }
    },
    deletedText: {
        background: theme.colors.pinka,
        textDecoration: `line-through`,
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap',
        lineHeight: `${TEXT_LINE_HEIGHT}px`
    },
    diffText: {
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap',
        lineHeight: `${TEXT_LINE_HEIGHT}px`
    },
    updatedText: {
        background: theme.colors.griptide
    },
    divider: {
        width: 1,
        height: 12,
        background: theme.colors.midGrey,
        padding: `0 !important`
    },
    dlFlex: {
        display: 'flex'
    },
    fullWidth: {
        width: '100%'
    },
    cursor: {
        cursor: 'pointer'
    }
}));

const DEPENDENCY_OPTIONS = [
    {
        value: DEPENDENCY_STATUS.UP_TO_DATE,
        label: i18n.t(`dependency_label_up_to_date`),
        icon: <DependencySVG color={theme.colors.atlantis} />,
        subField: '_dependencyStatus'
    },
    {
        value: DEPENDENCY_STATUS.OUT_OF_DATE,
        label: i18n.t(`dependency_label_out_of_date`),
        icon: <DependencySVG color={theme.colors.sun} />,
        subField: '_dependencyStatus'
    }
];

const SOURCE_STATUS_OPTIONS = [
    {
        value: SOURCE_STATUS.DO_NOT_TRANSLATE,
        label: i18n.t(`do_not_translate`),
        icon: getSourceStatusIcon({ status: SOURCE_STATUS.DO_NOT_TRANSLATE }),
        subField: '_sourceStatus'
    },
    {
        value: SOURCE_STATUS.NOT_READY_FOR_TRANSLATION,
        label: i18n.t(`not_ready_for_translation`),
        icon: getSourceStatusIcon({ status: SOURCE_STATUS.NOT_READY_FOR_TRANSLATION }),
        subField: '_sourceStatus'
    },
    {
        value: SOURCE_STATUS.READY_FOR_TRANSLATION,
        label: i18n.t(`ready_for_translation`),
        icon: getSourceStatusIcon({ status: SOURCE_STATUS.READY_FOR_TRANSLATION }),
        subField: '_sourceStatus'
    },
    {
        value: SOURCE_STATUS.LOCKED,
        label: i18n.t(`locked`),
        icon: getSourceStatusIcon({ status: SOURCE_STATUS.LOCKED }),
        subField: '_sourceStatus'
    }
];

function Translation({
    column,
    isReadOnly,
    rowId,
    columnId,
    rowIndex,
    columnIndex,
    data,
    originalValue,
    height,
    showDiff,
    setShowDiff
}) {
    const classes = useStyles({ height });
    const latestRecordHistoryData = useLatestRecordHistoryData();
    const memberMappingByEmail = useMemberMappingByEmail();
    const [rawText, setRawText] = React.useState(getEditorValueBySplitLineBreak(originalValue));
    const [showSpecialCharacter, setShowSpecialCharacter] = React.useState(false);
    const isEnterAlready = React.useRef(false);
    const dispatch = useDispatch();
    const editorRef = React.useRef();
    const rootRef = React.useRef();
    const { t } = useTranslation();
    const dependencies = useDependencyList();
    const disabledSourceColumns = useDisabledSourceColumns();
    const viewColumnIds = useViewColumnIdsWithReOrder();

    const value = React.useMemo(() => {
        if (typeof rawText !== 'object') return rawText;
        return serializeTag(rawText);
    }, [rawText]);

    const sources = React.useMemo(() => {
        return [columnId]?.filter(columnId => {
            const match = dependencies?.find(dpDc => dpDc?.parent === columnId);
            return !!match && match?.child && !disabledSourceColumns?.includes(columnId);
        });
    }, [columnId, dependencies, disabledSourceColumns]);

    const hasSource = React.useMemo(() => {
        return sources?.length > 0;
    }, [sources]);

    // const toggleShowDiff = React.useCallback(() => {
    //     setShowDiff(!showDiff);
    //     setShowSpecialCharacter(false);
    // }, [showDiff, setShowDiff]);

    const toggleSpecialCharacter = React.useCallback(() => {
        setShowDiff(false);
        setShowSpecialCharacter(!showSpecialCharacter);
    }, [showSpecialCharacter, setShowDiff]);

    const isSameData = React.useMemo(() => {
        if ((originalValue === null || !originalValue) && !value) return true;
        return value === originalValue;
    }, [originalValue, value]);

    useClickAwaitListener(
        rootRef,
        e => {
            if (!ReactEditor.isFocused(editorRef?.current)) {
                return false;
            }
            if (checkContainId(e, 'translate-special-character')) {
                return false;
            }

            handleCellClickAwayAndStay(e);
        },
        0
    );

    const handleCellClickAwayAndStay = React.useCallback(
        e => {
            if (isSameData) {
                return;
            }
            isEnterAlready.current = true;
            ReactEditor.blur(editorRef.current);
            dispatch(
                gridUIActions.cellClickAwayAndStay({
                    value,
                    rowId: rowId,
                    columnId: columnId,
                    type: columnTypes.MULTIPLE_LINES
                })
            );
        },
        [dispatch, value, columnId, rowId, isSameData]
    );

    React.useEffect(() => {
        const editor = editorRef.current;
        if (editor) {
            Transforms.select(editor, Editor.start(editor, []));
            // if (!isReadOnly) {
            //     ReactEditor.focus(editor);
            // }
            setRawText(getEditorValueBySplitLineBreak(originalValue));
            setTimeout(() => {
                Transforms.select(editor, Editor.end(editor, []));
            }, 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowId, columnId, isReadOnly]);

    const compareTextValue = React.useMemo(() => {
        const matchedColumn = latestRecordHistoryData?.cellHistories?.[columnId];
        if (!matchedColumn) return '';
        return matchedColumn?.previousData || '';
    }, [latestRecordHistoryData, columnId]);

    const handleMoveNextRow = React.useCallback(
        e => {
            e.preventDefault();
            if (isSameData) {
                dispatch(gridUIActions.cancelCellEdit());
                dispatch(gridUIActions.moveCellToNextRow({ rowIndex, columnIndex }));
                return;
            }
            isEnterAlready.current = true;
            dispatch(
                gridUIActions.cellClickAwayAndGoNextRow({
                    value,
                    rowId,
                    columnId,
                    rowIndex,
                    columnIndex
                })
            );
        },
        [columnId, rowId, columnIndex, rowIndex, dispatch, value, isSameData]
    );

    const handleMovePreviousRow = React.useCallback(
        e => {
            e.preventDefault();
            if (isSameData) {
                dispatch(gridUIActions.cancelCellEdit());
                dispatch(gridUIActions.moveCellToPreviousRow({ rowIndex, columnIndex }));
                return;
            }
            isEnterAlready.current = true;
            dispatch(
                gridUIActions.cellClickAwayAndGoPreviousRow({
                    value,
                    rowId,
                    columnId,
                    rowIndex,
                    columnIndex
                })
            );
        },
        [columnId, rowId, columnIndex, rowIndex, dispatch, value, isSameData]
    );

    const handleKeyDown = React.useCallback(
        e => {
            if (isKbEscape(e)) {
                dispatch(gridUIActions.cancelCellEdit());
            }

            if (isKbEnter(e) && !isKbShiftEnter(e)) {
                e.preventDefault();
                if (isSameData) {
                    dispatch(gridUIActions.cancelCellEdit());
                    dispatch(gridUIActions.moveCellToNextRow({ rowIndex, columnIndex }));
                    return;
                }
                isEnterAlready.current = true;
                dispatch(
                    gridUIActions.cellClickAwayAndGoNextRow({
                        value,
                        rowId,
                        columnId,
                        rowIndex,
                        columnIndex
                    })
                );
            }

            if (isKbTab(e)) {
                e.preventDefault();
                if (isSameData) {
                    dispatch(gridUIActions.cancelCellEdit());
                    dispatch(gridUIActions.moveCellToNextColumn({ rowIndex, columnIndex }));
                    return;
                }
                isEnterAlready.current = true;
                dispatch(
                    gridUIActions.cellClickAwayAndGoNextColumn({
                        value,
                        rowId,
                        columnId,
                        rowIndex,
                        columnIndex
                    })
                );
            }

            if (isKbShiftTab(e)) {
                e.preventDefault();
                if (isSameData) {
                    dispatch(gridUIActions.cancelCellEdit());
                    dispatch(gridUIActions.moveCellToPreviousColumn({ rowIndex, columnIndex }));
                    return;
                }
                isEnterAlready.current = true;
                dispatch(
                    gridUIActions.cellClickAwayAndGoPreviousColumn({
                        value,
                        rowId,
                        rowIndex,
                        columnId,
                        columnIndex
                    })
                );
            }

            if (isKbPageEnd(e)) {
                handleMoveNextRow(e);
                return;
            }

            if (isKbPageStart(e)) {
                handleMovePreviousRow(e);
                return;
            }
        },
        [columnId, rowId, columnIndex, rowIndex, dispatch, value, isSameData, handleMoveNextRow, handleMovePreviousRow]
    );

    const diffResult = React.useMemo(() => {
        return diff.diff_wordMode(compareTextValue || '', originalValue || '');
    }, [compareTextValue, originalValue]);

    const handleChange = React.useCallback(newRawText => {
        setRawText(newRawText);
    }, []);

    const targets = React.useMemo(() => {
        return dependencies
            ?.filter(dpDc => dpDc?.parent === columnId)
            ?.map(dpDc => dpDc?.child)
            ?.filter(columnId => viewColumnIds?.includes(columnId));
    }, [columnId, dependencies, viewColumnIds]);

    const hasChild = React.useMemo(() => {
        return targets?.length > 0;
    }, [targets]);

    const handleMarkTargetDependency = React.useCallback(
        status => {
            sendManualTrack({ type: `Mark Dependency Status As ${status}` });
            dispatch(gridUIActions.approveDependencyStatus(status, targets));
        },
        [dispatch, targets]
    );

    const handleMarkSourceStatus = React.useCallback(
        sourceStatus => {
            sendManualTrack({
                type: `Mark Source Status As ${getSourceStatusLabelByStatus({ status: sourceStatus })}`
            });
            dispatch(gridUIActions.approveSourceStatus(sourceStatus, [columnId]));
        },
        [dispatch, columnId]
    );

    return (
        <Grid className={classes.root} style={{ height }} container ref={rootRef}>
            <Grid container direction="column" className={classes.panel}>
                <Grid
                    item
                    container
                    className={classes.header}
                    direction="row"
                    alignItems="center"
                    justify="space-between"
                >
                    <Grid item alignItems="center" style={{ minHeight: 36, display: 'flex', alignItems: 'center' }}>
                        <ColumnTypeDisplay {...column} />
                    </Grid>
                    <Grid item>
                        <Grid container spacing={2}>
                            <Grid item>
                                <p className="caption inline">by</p>{' '}
                                <p className="body1 inline">
                                    {getFullName(memberMappingByEmail?.[latestRecordHistoryData?.alteredBy])}
                                </p>{' '}
                                <p className="caption inline">
                                    {getFriendlyTime(latestRecordHistoryData?.alteredTime, `MM-DD-YYYY hh:mm`)}
                                </p>{' '}
                            </Grid>
                            <TooltipArrows handleUp={handleMovePreviousRow} handleDown={handleMoveNextRow} />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item>
                    <div className={classes.inputWrapper}>
                        {showDiff ? (
                            <pre
                                className={`${classes.diff} ${isReadOnly ? classes.disabled : ``} `}
                                style={{
                                    height: height - 86 - 24 * 2 - 16 * 2,
                                    maxHeight: height - 86 - 24 * 2 - 16 * 2,
                                    overflowY: 'auto'
                                }}
                            >
                                {diffResult.map((item, index) => {
                                    const [status, content] = item;
                                    const getClassName = status => {
                                        switch (status) {
                                            case 0:
                                                return;
                                            case 1:
                                                return classes.updatedText;
                                            case -1:
                                                return classes.deletedText;
                                            default:
                                                return;
                                        }
                                    };
                                    return (
                                        <span key={index} className={getClassName(status)}>
                                            {content}
                                        </span>
                                    );
                                })}
                            </pre>
                        ) : (
                            <TokenEditor
                                value={rawText}
                                suggestions={[]}
                                parentTags={[]}
                                suggestionZIndex={1302}
                                onBlur={() => {}}
                                placeholder=""
                                className={`${classes.multiText} ${isReadOnly ? classes.disabled : ``} cell-editor`}
                                predefinedTokens={[]}
                                tokenDetection={[]}
                                style={{
                                    height: height - 86 - 24 * 2 - 16 * 2,
                                    maxHeight: height - 86 - 24 * 2 - 16 * 2
                                }}
                                // autoFocus={!isReadOnly}
                                inputRef={editorRef}
                                isHighlightSymbolCharacter={showSpecialCharacter}
                                readOnly={isReadOnly}
                                onKeyDown={handleKeyDown}
                                onChange={handleChange}
                            />
                        )}
                    </div>
                </Grid>
                <Grid item>
                    <Grid
                        className={classes.footer}
                        container
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Grid item>
                            <Grid container spacing={2} wrap="nowrap" alignItems="center">
                                <Grid item>
                                    <p className="body2 text-[12px] text-grey-mid">{value?.length} characters</p>
                                </Grid>
                                <Grid item className={classes.divider} />
                                <Grid item>
                                    <p className="body2 text-grey-mid">
                                        <span style={{ fontWeight: 600 }}>Shift + Return</span> to add a new line
                                    </p>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <Grid container direction="row" alignItems="center">
                                <ShowSpecialCharacter
                                    id="translate-special-character"
                                    showSpecialCharacter={showSpecialCharacter}
                                    onClick={toggleSpecialCharacter}
                                />
                                {/* <ShowDiffChange showDiff={showDiff} onClick={toggleShowDiff} /> */}
                                {isReadOnly && <ShowCellCannotEdit />}
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            {(hasSource || hasChild) && (
                <Grid
                    container
                    direction="column"
                    wrap="nowrap"
                    className={classes.sidebar}
                    style={{
                        maxHeight: height - 14,
                        overflowY: 'auto',
                        paddingBottom: theme.spacing(3)
                    }}
                >
                    {hasSource && (
                        <Grid item direction="column" spacing={2} className={classes.fullWidth}>
                            <Grid item>
                                <p className="body1">Source Status Options</p>
                            </Grid>
                            <Grid item>
                                <RadioGroup
                                    defaultValue={data?._sourceStatus}
                                    onChange={e => handleMarkSourceStatus(e?.target?.value)}
                                >
                                    <Grid container direction="column">
                                        {SOURCE_STATUS_OPTIONS.map(option => {
                                            return (
                                                <Grid item key={option?.value}>
                                                    <FormControlLabel
                                                        value={option?.value}
                                                        control={<Radio size="small" />}
                                                        label={
                                                            <Grid
                                                                container
                                                                wrap="nowrap"
                                                                direction="row"
                                                                alignItems="center"
                                                                spacing={1}
                                                            >
                                                                <Grid item className={classes.dlFlex}>
                                                                    {option.icon}
                                                                </Grid>
                                                                <Grid item>
                                                                    <p className="body2 whitespace-nowrap !font-normal">
                                                                        {option?.label}
                                                                    </p>
                                                                </Grid>
                                                            </Grid>
                                                        }
                                                    />
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </RadioGroup>
                            </Grid>
                        </Grid>
                    )}
                    {hasChild && (
                        <Grid item direction="column" container spacing={2}>
                            <Grid item>
                                <p className="body1">{t(`set_target_dependencies`)}</p>
                            </Grid>
                            {DEPENDENCY_OPTIONS.map(option => {
                                return (
                                    <Grid item key={option?.value}>
                                        <Grid container direction="row" justifyContent="space-between">
                                            <Grid item>
                                                <Grid
                                                    container
                                                    wrap="nowrap"
                                                    direction="row"
                                                    alignItems="center"
                                                    spacing={1}
                                                >
                                                    <Grid item className={classes.dlFlex}>
                                                        {option.icon}
                                                    </Grid>
                                                    <Grid item>
                                                        <p className="body2 whitespace-nowrap">{option?.label}</p>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                            <Grid
                                                item
                                                className={classes.cursor}
                                                onClick={() => handleMarkTargetDependency(option.value)}
                                            >
                                                <ApplyIconSVG />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                </Grid>
            )}
        </Grid>
    );
}

export default Translation;
